import { ReactElement } from 'react';
import { HiOutlineArrowPath, HiOutlineClock, HiOutlineExclamationTriangle } from 'react-icons/hi2';
import { RunReprocessInfo } from '../providers/reprocessContext';

/** Friendly labels for known pipeline stage names emitted by the backend. */
const STAGE_LABELS: Record<string, string> = {
  QUEUED: 'Queued',
  STARTED: 'Starting',
};

/**
 * Turn a raw `processing_stage` name (e.g. 'RouteMatching', 'route_pre_grid')
 * into a human-readable label. Falls back to splitting camelCase / snake_case.
 */
function humanizeStage(stage: string | null | undefined): string {
  if (!stage) return 'Processing';
  if (STAGE_LABELS[stage]) return STAGE_LABELS[stage];
  const spaced = stage.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

interface RunProgressIndicatorProps {
  /** Per-run reprocess state from `useReprocess().getRunProgress(runId)`. */
  info: RunReprocessInfo | undefined;
  /** 'inline' = compact icon + short label (table cells); 'full' = icon + full sentence. */
  variant?: 'inline' | 'full';
  className?: string;
}

interface IndicatorVisual {
  icon: ReactElement;
  shortLabel: string;
  fullLabel: string;
}

const ICON = 'cr-size-5 cr-flex-shrink-0';

function visualFor(info: RunReprocessInfo): IndicatorVisual {
  const stage = humanizeStage(info.stage);
  switch (info.phase) {
    case 'queued':
      return {
        icon: <HiOutlineClock className={`${ICON} cr-text-blue-500`} />,
        shortLabel: 'Queued',
        fullLabel: 'Queued for reprocessing',
      };
    case 'stalled':
      return {
        icon: <HiOutlineExclamationTriangle className={`${ICON} cr-text-orange-500`} />,
        shortLabel: 'Stalled',
        fullLabel: 'Reprocess stalled — no progress for over 5 minutes',
      };
    case 'error':
      return {
        icon: <HiOutlineExclamationTriangle className={`${ICON} cr-text-red-500`} />,
        shortLabel: 'Failed',
        fullLabel: 'Reprocess failed',
      };
    case 'processing':
    default:
      return {
        icon: <HiOutlineArrowPath className={`${ICON} cr-animate-spin cr-text-blue-600`} />,
        shortLabel: stage,
        fullLabel: `Reprocessing — ${stage}`,
      };
  }
}

/**
 * Live per-run reprocess indicator. Renders nothing when `info` is undefined
 * (run not being tracked), so callers may mount it unconditionally.
 */
export default function RunProgressIndicator({
  info,
  variant = 'inline',
  className = '',
}: RunProgressIndicatorProps): ReactElement | null {
  if (!info) return null;
  const { icon, shortLabel, fullLabel } = visualFor(info);

  if (variant === 'full') {
    const cls = `cr-flex cr-items-center cr-gap-2 cr-text-sm cr-text-gray-700 ${className}`;
    return (
      <div className={cls} title={fullLabel}>
        {icon}
        <span>{fullLabel}</span>
      </div>
    );
  }

  const cls = `cr-flex cr-items-center cr-gap-1 ${className}`;
  return (
    <div className={cls} title={fullLabel}>
      {icon}
      <span className="cr-text-xs cr-font-medium cr-text-gray-600">{shortLabel}</span>
    </div>
  );
}
